/**
 * AtlasProvider — single orchestration point.
 *
 * Responsibilities:
 *  - Derive AtlasContext (org, product, route, integration flags).
 *  - Maintain per-mission state (step, completed, skipped, snoozed).
 *  - Expose actions (start/skip/restart/completeCheckpoint).
 *  - Listen for storage changes across tabs and a private in-process bus.
 *
 * Mounted in `Layout.tsx` inside `PostUpdateJourneyProvider`.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { can, type PermissionKey } from '../../shared/permissions';
import { usePostUpdateJourneyOptional } from '../../shared/hooks/usePostUpdateJourney';
import {
  hasAbsenceKeywordsOpenedForProduct,
  hasCalendarSyncedForProduct,
  hasPublicHolidayKeywordsOpenedForProduct,
  hasVisitedReleasesForProduct,
  isWelcomeTourCompleted,
  markVisitedReleasesForProduct,
  isAtlasGuidanceDismissedForProduct,
  dismissAtlasGuidanceForProduct,
  clearAtlasGuidanceDismissedForProduct,
} from '../../shared/onboarding/onboardingState';
import {
  readMissionState,
  skipMission,
  completeMission,
  restartFromScratch,
  markFirstReportExported,
  markFirstBaselineRecorded,
  hasFirstReportExported,
  hasFirstBaselineRecorded,
  restoreAtlasNavigatorState,
} from './adapters/onboardingStateAdapter';
import type { AtlasContext, AtlasMission, AtlasMissionId, AtlasMissionState } from './atlas.types';
import { chooseCurrentMission } from './chooseCurrentMission';
import { ATLAS_MISSIONS, getMissionById } from './missions';
import { emitAtlas } from './telemetry';
import {
  DOM_EVENT_ATLAS_MISSION_CHANGE,
  DOM_EVENT_WELCOME_TOUR_COMPLETED,
} from '../../shared/config/storageKeys';
import { brand } from '../../shared/config/brand';

const P = brand.storagePrefix;

const ATLAS_BUS_EVENT = DOM_EVENT_ATLAS_MISSION_CHANGE;

interface AtlasContextValue {
  /** Derived context used by orchestration. */
  ctx: AtlasContext;
  /** Map of mission id -> current persisted state. */
  states: Record<AtlasMissionId, AtlasMissionState>;
  /** The mission Atlas recommends right now, or `null` when nothing is open. */
  currentMissionId: AtlasMissionId | null;
  currentMission: AtlasMission | null;
  /** All missions (for the Dock list). */
  missions: AtlasMission[];
  getState: (id: AtlasMissionId) => AtlasMissionState | undefined;
  /** Navigate to the mission's entry route (used by the Dock Continue CTA). */
  startMission: (id: AtlasMissionId) => void;
  skip: (id: AtlasMissionId) => void;
  complete: (id: AtlasMissionId) => void;
  restart: (id: AtlasMissionId) => void;
  /** Atlas checkpoints (used by pages to flip derived state). */
  recordFirstReport: () => void;
  recordFirstBaseline: () => void;
  /** Tells the provider to refresh state (e.g. after a localStorage mutation). */
  refresh: () => void;
  /** Hide the entire dock until `restoreNavigator` (user chose Close in the panel). */
  dismissGuidance: () => void;
  /** True when the user dismissed the dock for this org + product. */
  guidanceDismissed: boolean;
  /** Clears “Close Atlas” and per-mission skipped state so the dock and tours return. */
  restoreNavigator: () => void;
  /**
   * Permission predicate honoured by the Atlas registry. AtlasDock uses this
   * to drop permission-gated missions (e.g. `first-report` for Members) from
   * the checklist so the surfaced count matches what the user can actually
   * reach.
   */
  permissionPredicate: (key: string) => boolean;
}

const AtlasReactContext = createContext<AtlasContextValue | null>(null);

function emptyStates(): Record<AtlasMissionId, AtlasMissionState> {
  const out = {} as Record<AtlasMissionId, AtlasMissionState>;
  ATLAS_MISSIONS.forEach((m) => {
    out[m.id] = {
      missionId: m.id,
      step: undefined,
      completed: false,
      skipped: false,
      snoozedUntil: null,
    };
  });
  return out;
}

interface ComputeStatesFlags {
  hasSharePoint: boolean;
}

function computeStates(
  orgId: string | null,
  productId: string | null,
  flags: ComputeStatesFlags
): Record<AtlasMissionId, AtlasMissionState> {
  const derived = {
    hasOrg: Boolean(orgId),
    hasProduct: Boolean(productId),
    hasSharePoint: flags.hasSharePoint,
    hasCalendarSynced: orgId && productId ? hasCalendarSyncedForProduct(orgId, productId) : false,
    hasOpenedAbsenceKeywords:
      orgId && productId ? hasAbsenceKeywordsOpenedForProduct(orgId, productId) : false,
    hasOpenedPublicHolidayKeywords:
      orgId && productId ? hasPublicHolidayKeywordsOpenedForProduct(orgId, productId) : false,
    hasVisitedReleases: orgId && productId ? hasVisitedReleasesForProduct(orgId, productId) : false,
  };
  const out = emptyStates();
  // Derived missions resolve from context alone — no org/product required.
  ATLAS_MISSIONS.forEach((m) => {
    if (m.storage === 'derived') {
      // Use empty strings for orgId/productId — derived readers ignore them.
      out[m.id] = readMissionState(m, orgId ?? '', productId ?? '', derived);
    }
  });
  if (!orgId || !productId) return out;
  ATLAS_MISSIONS.forEach((m) => {
    if (m.storage === 'derived') return;
    out[m.id] = readMissionState(m, orgId, productId, derived);
  });
  return out;
}

export const AtlasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentOrg, currentProduct, organizations, effectiveRole } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const journey = usePostUpdateJourneyOptional();

  const orgId = currentOrg?.id ?? null;
  const productId = currentProduct?.id ?? null;

  // Integration + data flags derived from auth state. Conservative defaults —
  // when unknown we assume FALSE so Atlas nudges the user to configure.
  const hasJira = useMemo(() => {
    // Heuristic: any product with a jira_project_key, or an org-level integration flag.
    if (!currentOrg) return false;
    const anyProductWithKey = (currentOrg.products ?? []).some(
      (p) => typeof p.jira_project_key === 'string' && p.jira_project_key.trim().length > 0
    );
    return anyProductWithKey;
  }, [currentOrg]);

  const hasSharePoint = useMemo(() => {
    if (!currentProduct) return false;
    return Boolean(currentProduct.sharepoint_calendar_url || currentProduct.sharepoint_list_id);
  }, [currentProduct]);

  // hasAnalyticsData is a soft proxy: welcome tour completed AND post-update
  // journey is not in a pre-data state. Pages can refine via checkpoints.
  const hasAnalyticsData = useMemo(() => {
    if (!journey) return isWelcomeTourCompleted();
    const step = journey.currentStep;
    if (!step) return isWelcomeTourCompleted();
    return step === 'completed' || step === 'skipped';
  }, [journey]);

  const [refreshTick, setRefreshTick] = useState(0);

  const guidanceDismissed = useMemo(
    () => (orgId ? isAtlasGuidanceDismissedForProduct(orgId, productId) : false),
    // refreshTick: dismiss flag is in localStorage
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, productId, refreshTick]
  );

  const ctx: AtlasContext = useMemo(
    () => ({
      orgId,
      productId,
      pathname,
      hasOrg: orgId != null,
      hasProduct: productId != null,
      hasJira,
      hasSharePoint,
      hasAnalyticsData,
      postUpdateJourneyActive: Boolean(
        journey?.currentStep &&
          journey.currentStep !== 'completed' &&
          journey.currentStep !== 'skipped'
      ),
      welcomeCompleted: isWelcomeTourCompleted(),
      hasCalendarSynced: orgId && productId ? hasCalendarSyncedForProduct(orgId, productId) : false,
      hasOpenedAbsenceKeywords:
        orgId && productId ? hasAbsenceKeywordsOpenedForProduct(orgId, productId) : false,
      hasOpenedPublicHolidayKeywords:
        orgId && productId ? hasPublicHolidayKeywordsOpenedForProduct(orgId, productId) : false,
      hasVisitedReleases:
        orgId && productId ? hasVisitedReleasesForProduct(orgId, productId) : false,
    }),
    // refreshTick lets checkpoint writes (localStorage) re-enter the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, productId, pathname, hasJira, hasSharePoint, hasAnalyticsData, journey, refreshTick]
  );

  const states = useMemo(
    () => computeStates(orgId, productId, { hasSharePoint }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, productId, hasSharePoint, refreshTick, organizations]
  );

  // Permission predicate threads the live user role into mission selection.
  // Missions with `requiredPermission` set (e.g. `first-report`) are filtered
  // out for users who don't hold the corresponding permission — keeps the
  // Reports tour off Member dashboards.
  const permissionPredicate = useCallback(
    (key: string) => can(effectiveRole, key as PermissionKey),
    [effectiveRole]
  );

  const currentMissionId = useMemo(
    () => chooseCurrentMission(ctx, states, Date.now(), permissionPredicate),
    [ctx, states, permissionPredicate]
  );

  const currentMission = useMemo(
    () => (currentMissionId ? (getMissionById(currentMissionId) ?? null) : null),
    [currentMissionId]
  );

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  // React to localStorage changes from other tabs and our internal bus.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (
        e.key.startsWith(`${P}.onboarding`) ||
        e.key.startsWith(`${P}.coach.`) ||
        e.key.startsWith(`${P}.atlas`)
      ) {
        refresh();
      }
    };
    const onBus = () => refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener(ATLAS_BUS_EVENT, onBus as EventListener);
    window.addEventListener(DOM_EVENT_WELCOME_TOUR_COMPLETED, onBus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ATLAS_BUS_EVENT, onBus as EventListener);
      window.removeEventListener(DOM_EVENT_WELCOME_TOUR_COMPLETED, onBus);
    };
  }, [refresh]);

  const publish = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent(ATLAS_BUS_EVENT));
    } catch {
      // ignore
    }
  }, []);

  // Mark the "visited Releases" checkpoint on any /releases visit. Covers both
  // the calendar tour's "Open Releases" button and any unrelated navigation —
  // either counts per product UX decision.
  useEffect(() => {
    if (!orgId || !productId) return;
    if (pathname !== '/releases' && !pathname.startsWith('/releases/')) return;
    if (hasVisitedReleasesForProduct(orgId, productId)) return;
    markVisitedReleasesForProduct(orgId, productId);
    refresh();
    publish();
  }, [pathname, orgId, productId, refresh, publish]);

  const getState = useCallback((id: AtlasMissionId) => states[id], [states]);

  const startMission = useCallback(
    (id: AtlasMissionId) => {
      const mission = getMissionById(id);
      if (!mission) return;
      emitAtlas('atlas.mission.started', {
        missionId: id,
        orgId,
        productId,
        reason: 'dock',
      });
      // Journey mission: resume on the page owning the current step.
      if (mission.storage === 'journey' && journey?.currentStep) {
        const step = journey.currentStep;
        let target = mission.entryRoute;
        if (step === 'navigateCalendar') target = '/calendar';
        else if (step === 'navigateReleases') target = '/releases';
        else target = '/analytics';
        if (pathname !== target) navigate(target);
        return;
      }
      if (mission.entryRoute && pathname !== mission.entryRoute) {
        navigate(mission.entryRoute);
      }
    },
    [orgId, productId, pathname, navigate, journey]
  );

  const skip = useCallback(
    (id: AtlasMissionId) => {
      const mission = getMissionById(id);
      if (!mission || !orgId || !productId) return;
      skipMission(mission, orgId, productId);
      emitAtlas('atlas.mission.skipped', { missionId: id, orgId, productId });
      refresh();
      publish();
    },
    [orgId, productId, refresh, publish]
  );

  const complete = useCallback(
    (id: AtlasMissionId) => {
      const mission = getMissionById(id);
      if (!mission || !orgId || !productId) return;
      completeMission(mission, orgId, productId);
      emitAtlas('atlas.mission.completed', {
        missionId: id,
        orgId,
        productId,
        reason: 'manual',
      });
      refresh();
      publish();
    },
    [orgId, productId, refresh, publish]
  );

  const restart = useCallback(
    (id: AtlasMissionId) => {
      const mission = getMissionById(id);
      if (!mission || !orgId || !productId) return;
      restartFromScratch(mission, orgId, productId);
      refresh();
      publish();
    },
    [orgId, productId, refresh, publish]
  );

  const recordFirstReport = useCallback(() => {
    if (!orgId || !productId) return;
    if (hasFirstReportExported(orgId, productId)) return;
    markFirstReportExported(orgId, productId);
    const mission = getMissionById('first-report');
    if (mission) completeMission(mission, orgId, productId);
    refresh();
    publish();
  }, [orgId, productId, refresh, publish]);

  const recordFirstBaseline = useCallback(() => {
    if (!orgId || !productId) return;
    if (hasFirstBaselineRecorded(orgId, productId)) return;
    markFirstBaselineRecorded(orgId, productId);
    const mission = getMissionById('first-baseline');
    if (mission) completeMission(mission, orgId, productId);
    refresh();
    publish();
  }, [orgId, productId, refresh, publish]);

  const dismissGuidance = useCallback(() => {
    if (!orgId) return;
    dismissAtlasGuidanceForProduct(orgId, productId);
    emitAtlas('atlas.guidance.dismissed', { orgId, productId });
    refresh();
    publish();
  }, [orgId, productId, refresh, publish]);

  const restoreNavigator = useCallback(() => {
    if (!orgId) return;
    clearAtlasGuidanceDismissedForProduct(orgId, productId);
    if (productId) {
      restoreAtlasNavigatorState(orgId, productId);
    }
    emitAtlas('atlas.navigator.restored', { orgId, productId });
    refresh();
    publish();
  }, [orgId, productId, refresh, publish]);

  const value: AtlasContextValue = useMemo(
    () => ({
      ctx,
      states,
      currentMissionId,
      currentMission,
      missions: ATLAS_MISSIONS,
      getState,
      startMission,
      skip,
      complete,
      restart,
      recordFirstReport,
      recordFirstBaseline,
      refresh,
      dismissGuidance,
      guidanceDismissed,
      restoreNavigator,
      permissionPredicate,
    }),
    [
      ctx,
      states,
      currentMissionId,
      currentMission,
      getState,
      startMission,
      skip,
      complete,
      restart,
      recordFirstReport,
      recordFirstBaseline,
      refresh,
      dismissGuidance,
      guidanceDismissed,
      restoreNavigator,
      permissionPredicate,
    ]
  );

  return <AtlasReactContext.Provider value={value}>{children}</AtlasReactContext.Provider>;
};

export function useAtlasOptional(): AtlasContextValue | null {
  return useContext(AtlasReactContext);
}

export function useAtlas(): AtlasContextValue {
  const v = useContext(AtlasReactContext);
  if (!v) {
    throw new Error('useAtlas must be used inside <AtlasProvider>.');
  }
  return v;
}
